import { and } from '@lumapps/utils/function/predicate/and';
import { or } from '@lumapps/utils/function/predicate/or';
import { createParagraph } from '@lumapps/wrex-typography/utils/createParagraph';
import { isParagraph } from '@lumapps/wrex-typography/utils/isParagraph';
import { HTMLElementConvertOption } from '@lumapps/wrex/serialization/html/fromHTML/types';
import { matchAttribute, matchNode, matchStyle } from '@lumapps/wrex/serialization/html/fromHTML/utils';

import { LIST_ITEM, ORDERED_LIST, UNORDERED_LIST } from '../../../constants';
import { createListItem } from '../../../utils/createListItem';
import { createOrderedList } from '../../../utils/createOrderedList';
import { createUnorderedList } from '../../../utils/createUnorderedList';
import { isList } from '../../../utils/isList';

type ElementTypes = typeof UNORDERED_LIST | typeof ORDERED_LIST | typeof LIST_ITEM;

/**
 * Element convert options.
 *
 * Warning: the order of these options does matter!
 */
export const ELEMENTS: { [Element in ElementTypes]: HTMLElementConvertOption<Element> } = {
    'unordered-list': {
        test: or(
            matchNode({ tagName: 'ul' }),
            and(
                matchAttribute('className', /MsoListParagraph/i),
                matchStyle('mso-list' as any, (listValue: string) => /\bl0\b/i.test(listValue)),
            ),
        ),
        type: UNORDERED_LIST,
        transform: (node, children: any) => {
            if (matchStyle('mso-list' as any)(node)) {
                // Remove the two unecessary children of ms list (a ° and an empty space)
                const listItemChildren = children.slice(2);
                const listItemParagraph = createParagraph(listItemChildren);

                return createUnorderedList([createListItem([listItemParagraph])]);
            }
            return createUnorderedList(children);
        },
    },
    'ordered-list': {
        test: or(
            matchNode({ tagName: 'ol' }),
            and(
                matchAttribute('className', /MsoListParagraph/i),
                matchStyle('mso-list' as any, (listValue: string) => /\bl1\b/i.test(listValue)),
            ),
        ),
        type: ORDERED_LIST,
        transform: (node, children: any) => {
            if (matchStyle('mso-list' as any)(node)) {
                // Remove the two unecessary children of ms list (a ° and an empty space)
                const listItemChildren = children.slice(2);
                const listItemParagraph = createParagraph(listItemChildren);

                return createOrderedList([createListItem([listItemParagraph])]);
            }
            return createOrderedList(children);
        },
    },
    'list-item': {
        test: matchNode({ tagName: 'li' }),
        type: LIST_ITEM,
        transform: (_, children: any) => {
            const initChildren = children;
            // Handle empty parent case (<ul><li><br><li><ul><li>Child</li></ul></ul>)
            // We want to get rid of the newline, an empty paragraph is enough
            if (initChildren.length > 0 && initChildren[0].text === '\n') {
                initChildren[0].text = '';
            }

            // Handle multi line (<li><span>First part</span><span> second part</span></li>)
            // Group consecutive inline children (text nodes and inline elements) into paragraphs.
            const cleanedChildren = [];
            let inlineNodes = [];

            for (const child of initChildren) {
                if (isParagraph(child) || isList(child)) {
                    if (inlineNodes.length > 0) {
                        cleanedChildren.push(createParagraph(inlineNodes));
                        inlineNodes = [];
                    }
                    cleanedChildren.push(child);
                } else {
                    inlineNodes.push(child);
                }
            }

            if (inlineNodes.length > 0) {
                cleanedChildren.push(createParagraph(inlineNodes));
            }

            return createListItem(cleanedChildren);
        },
    },
};
